/**
 * Client slice (SCRUM-1017) — createSlice/createAppAsyncThunk migration of
 * clientReducer + clientActions. Boy-scout follow-up to SCRUM-1016 (getClient was
 * already migrated). Bare-RTK standard (SCRUM-652); mirrors historiesPagesSlice.js
 * (SCRUM-1014).
 *
 * clientActions.js re-exports everything here (shim), so the Client / Product /
 * Ads screens are unchanged. Every call site is fire-and-forget, so the thunks
 * need no `.unwrap()` wrappers — only thin wrappers to adapt the two multi-arg
 * signatures (deleteClient(id, countrycode), updateClient(data, id)).
 *
 * Two bugs fixed in the same pass:
 *  - getClients did `res.data.reverse()` with NO `.catch` — an unhandled rejection
 *    on failure, and a crash if the response was not an array. Now guarded + it
 *    rejects to the central hub.
 *  - deleteClient dispatched `GET_ERRORS: null` (generic "An error occurred"); it
 *    now surfaces the real server error via the hub. (addClient/updateClient's old
 *    `err.response.data` catch also crashed on a network error where err.response
 *    is undefined — createAppAsyncThunk's rejectWithValue guards that too.)
 *
 * Cross-slice: getClientsByCountryCode keeps dispatching the legacy
 * `GET_CLIENTS_BY_COUNTRYCODE` string because productReducer ALSO handles it.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { AxiosResponse } from "axios";
import api from "../api";
import createAppAsyncThunk from "../createAppAsyncThunk";
import { GET_CLIENTS_BY_COUNTRYCODE } from "./clientTypes";
import type { AppDispatch } from "../store";
import type {
  ClientsResponse,
  ClientByIdResponse,
  ProductsResponse,
  MessageResponse,
  ClientRecoverResponse,
} from "../../../../shared/apiTypes";

type ClientsState = {
  clients: any[];
  products: any[];
  client: any;
  message: string;
  clientsCountryCode: any[];
};

/** The form values a client is created or updated from. */
type ClientFormData = Record<string, unknown>;

const initialState: ClientsState = {
  clients: [],
  products: [],
  client: {},
  message: "",
  clientsCountryCode: [],
};

// ---------------------------------------------------------------------------
// Thunks — state writes ride .fulfilled below; on failure they reject and the
// central appErrorMiddleware surfaces the error (no per-thunk catch).
// ---------------------------------------------------------------------------

export const getClients = createAppAsyncThunk("clients/getAll", async (_arg: void) => {
  const res: AxiosResponse<ClientsResponse> = await api.get("/api/clients/");
  // SCRUM-1017: guard the reverse — a non-array error body used to crash here —
  // and let a failure reject to the hub instead of going unhandled.
  return Array.isArray(res.data) ? [...res.data].reverse() : [];
});

export const getClient = createAppAsyncThunk(
  "clients/getById",
  async (id: string) => {
    const res: AxiosResponse<ClientByIdResponse> = await api.get(`/api/clients/${id}`);
    return res.data;
  }
);

export const getClientProducts = createAppAsyncThunk(
  "clients/getProducts",
  async (idclient: string) => {
    const res: AxiosResponse<ProductsResponse> = await api.get(`/api/clients/products/${idclient}`);
    return res.data;
  }
);

export const getClientsByCountryCode = createAppAsyncThunk(
  "clients/getByCountry",
  async (countrycode: string, { dispatch }) => {
    const res: AxiosResponse<ClientsResponse> = await api.get(`/api/clients/clients/${countrycode}`);
    // Cross-slice: productReducer also has a case for this legacy string.
    dispatch({ type: GET_CLIENTS_BY_COUNTRYCODE, payload: res.data });
    return res.data;
  }
);

const addClientThunk = createAppAsyncThunk(
  "clients/add",
  async (data: ClientFormData) => {
    const res: AxiosResponse<MessageResponse> = await api.post("/api/clients/add", data);
    return res.data;
  }
);
export const addClient = (data: ClientFormData) => (dispatch: AppDispatch) =>
  dispatch(addClientThunk(data));

const updateClientThunk = createAppAsyncThunk(
  "clients/update",
  async (
    { data, id }: { data: ClientFormData; id: string },
    { dispatch }
  ) => {
    const res: AxiosResponse<MessageResponse> = await api.put(`/api/clients/update/${id}`, data);
    dispatch(getClient(id));
    return res.data;
  }
);
export const updateClient = (data: ClientFormData, id: string) => (dispatch: AppDispatch) =>
  dispatch(updateClientThunk({ data, id }));

// SCRUM-1818: the two answers to the 409 addClient gets when only a deleted
// client holds the name. Both reject to the central hub on failure, so the
// form keeps its offer on screen; callers `.unwrap()` them.

// Restore: bring the deleted client back under its reserved name.
export const restoreClient = createAppAsyncThunk(
  "clients/restore",
  async (id: string, { dispatch }) => {
    const res: AxiosResponse<ClientRecoverResponse> = await api.post(`/api/clients/${id}/restore`);
    dispatch(getClients());
    return res.data;
  }
);

// Start fresh: release the deleted client's name for a new client.
export const releaseClient = createAppAsyncThunk(
  "clients/release",
  async (id: string) => {
    const res: AxiosResponse<ClientRecoverResponse> = await api.post(`/api/clients/${id}/release`);
    return res.data;
  }
);

const deleteClientThunk = createAppAsyncThunk(
  "clients/delete",
  async (
    { id, countrycode }: { id: string; countrycode: string },
    { dispatch }
  ) => {
    await api.delete<unknown>(`/api/clients/delete/${id}`);
    dispatch(getClients());
    dispatch(getClientsByCountryCode(countrycode));
    return id;
  }
);
export const deleteClient = (id: string, countrycode: string) => (dispatch: AppDispatch) =>
  dispatch(deleteClientThunk({ id, countrycode }));

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    messageCleared(state) {
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClients.fulfilled, (state, action) => {
        state.clients = action.payload;
      })
      .addCase(getClient.fulfilled, (state, action) => {
        state.client = action.payload;
      })
      .addCase(getClientProducts.fulfilled, (state, action) => {
        state.products = action.payload;
      })
      .addCase(addClientThunk.fulfilled, (state, action) => {
        state.message = action.payload?.msg;
      })
      .addCase(updateClientThunk.fulfilled, (state, action) => {
        state.message = action.payload?.msg;
      })
      // Cross-slice legacy string (productReducer handles it too).
      .addCase(GET_CLIENTS_BY_COUNTRYCODE, (state, action: PayloadAction<ClientsState["clientsCountryCode"], typeof GET_CLIENTS_BY_COUNTRYCODE>) => {
        state.clientsCountryCode = action.payload;
      });
  },
});

// Clear the success/status message. A plain slice action now.
export const clearMessage = () => clientsSlice.actions.messageCleared();

// Exposed for tests that assert on the async action lifecycle / state.

export { initialState };
export default clientsSlice.reducer;
